/**
 * LaunchReadinessPanel — TAL-1
 *
 * Admin-only right-side panel with critical items checklist,
 * progress bar, and next actions.
 */

import React from 'react';
import type { TalentEntry } from './TalentRow';

export interface LaunchReadiness {
    critical_items: TalentEntry[];
    readiness_pct: number;
    next_actions: string[];
}

interface LaunchReadinessPanelProps {
    readiness: LaunchReadiness;
}

export const LaunchReadinessPanel: React.FC<LaunchReadinessPanelProps> = ({ readiness }) => {
    const remaining = readiness.critical_items.length;

    return (
        <div className="border border-gray-800 rounded bg-gray-900/30 p-3">
            <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] font-mono uppercase tracking-wider text-gray-400">
                    Launch Readiness
                </span>
                <span className="text-[10px] font-mono text-amber-400">
                    {remaining} remaining
                </span>
            </div>

            {/* Progress bar */}
            <div className="w-full bg-gray-800 rounded-full h-1.5 mb-3">
                <div
                    className="bg-[#39FF14] h-1.5 rounded-full transition-all"
                    style={{ width: `${Math.min(readiness.readiness_pct, 100)}%` }}
                />
            </div>

            {/* Critical items checklist */}
            <div className="space-y-1.5 mb-3">
                {readiness.critical_items.map((item) => (
                    <div
                        key={item.slug}
                        className="flex items-center gap-2 text-[11px] font-mono"
                    >
                        <span className={item.source === 'wiring' ? 'text-amber-400' : 'text-blue-400'}>
                            {item.source === 'wiring' ? '●' : '○'}
                        </span>
                        <span className="text-gray-300 flex-1 truncate">{item.name}</span>
                        {item.estimated_loc && (
                            <span className="text-gray-600 text-[9px]">~{item.estimated_loc} LOC</span>
                        )}
                    </div>
                ))}
            </div>

            {/* Next actions */}
            {readiness.next_actions.length > 0 && (
                <div className="border-t border-gray-800 pt-2 mt-2">
                    <span className="text-[9px] font-mono text-gray-600 uppercase">Next Actions</span>
                    <ul className="mt-1 space-y-0.5">
                        {readiness.next_actions.slice(0, 5).map((action, i) => (
                            <li key={i} className="text-[10px] font-mono text-gray-500">
                                → {action}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};
